import * as React from "react";
import styled from "styled-components";
import { IconButton } from "@8x8/oxygen-icon-button";
import { CheckIcon as OxygenCheckIcon } from "@8x8/oxygen-icon";
import Tooltip, { orientation, showOn } from "@8x8/oxygen-tooltip";

import pbxMapToOptions from "../pbx/pbxMapToOptions";
import tenantDataToOptions from "../tenant/tenantDataToOptions";
import InfoRow from "../InfoRow";
import PbxRow from "../PbxRow";
import TenantRow from "../TenantRow";
import { ActionBox, DefaultIcon, InfoBox } from "../styled";
import extractBaseCustomerInfo from "../extractBaseCustomerInfo";

const Container = styled.div`
  display: flex;
  padding: 8px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d3d3d3;
  box-shadow: ${({ theme }) => theme.shadowLow};
`;

const ResultCard = ({ customerData, menuPortalTarget, onCustomerSelected }) => {
  const baseInfo = React.useMemo(
    () => extractBaseCustomerInfo(customerData),
    [customerData]
  );
  const pbxOptions = pbxMapToOptions(customerData?.pbxMap ?? {});
  const tenantOptions = tenantDataToOptions(customerData?.tenantData ?? []);
  const [selectedPbx, setSelectedPbx] = React.useState(pbxOptions?.[0] ?? null);
  const [selectedTenant, setSelectedTenant] = React.useState(
    tenantOptions?.[0] ?? null
  );

  const augmentAndSelectCustomer = React.useCallback(() => {
    onCustomerSelected({
      ...customerData,
      /*
       * Those are custom keys that will tell our parent components which PBX
       * and/or tenant the user actually pre-selected, so we can load that one.
       * Some tools will ignore this, but we will provide it anyway.
       *
       * See {@link useManagedPbxSelection.js}.
       */
      preselectedPbxId: selectedPbx?.value ?? null,
      preselectedTenantId: selectedTenant?.value ?? null,
    });
  }, [onCustomerSelected, customerData, selectedPbx, selectedTenant]);

  return (
    <Container>
      <InfoBox>
        {baseInfo.map((item) => (
          <InfoRow
            renderIcon={() => <DefaultIcon />}
            attributeName={item.displayName}
            value={item.value}
            key={item.key}
          />
        ))}
        <PbxRow
          pbxOptions={pbxOptions}
          selectedPbx={selectedPbx}
          setSelectedPbx={setSelectedPbx}
          menuPortalTarget={menuPortalTarget}
        />
        <TenantRow
          tenantOptions={tenantOptions}
          selectedTenant={selectedTenant}
          setSelectedTenant={setSelectedTenant}
          menuPortalTarget={menuPortalTarget}
        />
      </InfoBox>
      <ActionBox>
        <Tooltip
          orientation={orientation.top}
          showOn={showOn.hover}
          title="Select this customer"
        >
          <IconButton onClick={augmentAndSelectCustomer}>
            <OxygenCheckIcon />
          </IconButton>
        </Tooltip>
      </ActionBox>
    </Container>
  );
};

export default ResultCard;
